import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Field, inputCls } from '../../components/ui';
import { type Brigade, type Page, directoryRequest, queryString } from '../../lib/personnel-api';
import { ErrorNotice } from './parts';

export function useBrigadeOptions(purpose: 'assignment' | 'transfer', enabled: boolean) {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const result = useInfiniteQuery({
    queryKey: ['personnel', 'brigade-options', purpose, query],
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      directoryRequest<Page<Brigade>>(
        `/api/brigades${queryString({ name: query || undefined, cursor: pageParam })}`,
      ),
    getNextPageParam: (last) => last.data.nextCursor ?? undefined,
  });
  const items = (result.data?.pages.flatMap((page) => page.data.items) ?? []).filter(
    (item, index, all) => all.findIndex((other) => other.id === item.id) === index,
  );
  return { ...result, items, search, setSearch, searchByName: () => setQuery(search.trim()) };
}

export function BrigadePicker({
  label,
  searchLabel,
  options,
  value,
  selected,
  onSelect,
  excludeId,
  requiredAction,
}: {
  label: string;
  searchLabel: string;
  options: ReturnType<typeof useBrigadeOptions>;
  value: string;
  selected: Brigade | null;
  onSelect: (brigade: Brigade | null) => void;
  excludeId?: string;
  requiredAction?: string;
}) {
  const visible = options.items.filter(
    (brigade) =>
      brigade.id !== excludeId && (!requiredAction || brigade.allowedActions?.includes(requiredAction)),
  );
  const retained =
    selected?.id === value && !visible.some((brigade) => brigade.id === value) ? selected : null;
  const choices = retained ? [retained, ...visible] : visible;
  return (
    <div className="space-y-3">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          options.searchByName();
        }}
      >
        <div className="min-w-0 flex-1 basis-52">
          <Field label={searchLabel} hint="Поиск по названию среди доступных бригад">
            <input
              aria-label={searchLabel}
              className={inputCls}
              value={options.search}
              onChange={(event) => options.setSearch(event.target.value)}
            />
          </Field>
        </div>
        <Button type="submit" disabled={options.isFetching}>
          Найти бригаду
        </Button>
      </form>
      <Field label={label}>
        <select
          aria-label={label}
          className={inputCls}
          value={value}
          onChange={(event) => onSelect(choices.find((brigade) => brigade.id === event.target.value) ?? null)}
        >
          <option value="">Выберите доступную бригаду</option>
          {choices.map((brigade) => (
            <option key={brigade.id} value={brigade.id}>
              {brigade.name} · {brigade.id.slice(0, 8)}
              {retained?.id === brigade.id ? ' · выбрана' : ''}
            </option>
          ))}
        </select>
      </Field>
      <ErrorNotice error={options.error} retry={() => void options.refetch()} />
      <p className="text-xs text-ink-3" role="status">
        {options.isLoading
          ? 'Загрузка доступных бригад…'
          : options.isFetchingNextPage
            ? 'Загрузка следующей страницы бригад…'
            : !visible.length && !options.error
              ? 'Других бригад по этому поиску не найдено.'
              : `Загружено бригад: ${visible.length}`}
      </p>
      {options.hasNextPage && (
        <Button disabled={options.isFetching} onClick={() => void options.fetchNextPage()}>
          Загрузить ещё бригады
        </Button>
      )}
    </div>
  );
}
